import { LocalizationPipe, LocalizationService } from '@abp/ng.core';
import { ToasterService } from '@abp/ng.theme.shared';
import { Component, computed, inject, signal } from '@angular/core';
import { FormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import {
  DEFAULT_TOASTER_LIFE,
  IWizardContext,
  WizardStepComponent,
  ZendraLocalizationServices,
} from '@zendra/shared';

import type { IFusionStand } from '../interfaces/IFusionStand';
import type { IDescriptionRow } from '../interfaces/IDescriptionRow';
import type { IResultingFusionStand } from '../interfaces/IResultingFusionStand';
import type { IStepCreateAreaMaintenanceData } from '../interfaces/IStepCreateAreaMaintenanceData';
import type { IStepDescriptionAndCompletionData } from '../interfaces/IStepDescriptionAndCompletionData';
import type { IStepSelectStandsToFuseData } from '../interfaces/IStepSelectStandsToFuseData';

@Component({
  selector: 'lib-step-description-and-completion-stand-fusion',
  standalone: true,
  imports: [LocalizationPipe, ReactiveFormsModule],
  templateUrl: './step-description-and-completion.html',
})
export class StepDescriptionAndCompletion
  implements WizardStepComponent<IStepDescriptionAndCompletionData>
{
  private readonly formBuilder = inject(FormBuilder);
  private readonly toaster = inject(ToasterService);
  private readonly localizationService = inject(LocalizationService);

  readonly localizationKeyPrefix = 'Geographic::StandFusion.StepDescriptionAndCompletion';
  readonly previousStepId = 3;

  readonly selectedStands = signal<IFusionStand[]>([]);
  readonly resultingStand = signal<IResultingFusionStand | null>(null);

  readonly form = this.formBuilder.group({
    transformationReason: ['', [Validators.required]],
  });

  readonly initialRows = computed<IDescriptionRow[]>(() =>
    this.selectedStands().map(row => ({
      id: row.id,
      stand: row.name,
      cycle: row.cycle,
      isLastCycle: row.isLastCycle,
      rotation: row.rotation,
      isLastRotation: row.isLastRotation,
      area: row.area,
    })),
  );

  readonly finalRows = computed<IDescriptionRow[]>(() => {
    const resultingStand = this.resultingStand();
    if (!resultingStand) {
      return [];
    }

    return [
      {
        id: resultingStand.standId ?? 'resulting-stand',
        stand: resultingStand.standName,
        cycle: resultingStand.cycle,
        isLastCycle: !!resultingStand.isLastCycle,
        rotation: resultingStand.rotation,
        isLastRotation: !!resultingStand.isLastRotation,
        area: resultingStand.adjustedArea,
      },
    ];
  });

  readonly initialTotalArea = computed(() =>
    this.roundArea(this.initialRows().reduce((sum, row) => sum + row.area, 0)),
  );
  readonly finalTotalArea = computed(() =>
    this.roundArea(this.finalRows().reduce((sum, row) => sum + row.area, 0)),
  );

  onActivate(context: IWizardContext): void {
    context.patchStepState(context.activeStepId(), {
      visited: true,
    });

    const selectedStandsData =
      context.getStepState<IStepSelectStandsToFuseData>(2)?.data;
    const previousStepData =
      context.getStepState<IStepCreateAreaMaintenanceData>(this.previousStepId)?.data;
    const currentState =
      context.getStepState<IStepDescriptionAndCompletionData>(context.activeStepId())?.data;
    const sourceStep3Snapshot = this.buildStep3Snapshot(previousStepData);
    const shouldRestoreCurrentData =
      !!currentState && currentState.sourceStep3Snapshot === sourceStep3Snapshot;

    this.selectedStands.set(selectedStandsData?.selectedStands ?? []);
    this.resultingStand.set(selectedStandsData?.resultingStand ?? null);

    if (!shouldRestoreCurrentData && currentState) {
      context.patchStepState(context.activeStepId(), {
        valid: false,
        completed: false,
        data: undefined,
        summary: [],
      });
    }

    this.form.reset(
      {
        transformationReason: shouldRestoreCurrentData
          ? currentState?.transformationReason ?? ''
          : '',
      },
      { emitEvent: false },
    );
  }

  canLeave(): boolean {
    this.form.markAllAsTouched();

    if (this.form.valid) {
      return true;
    }

    this.toaster.error(
      `${ZendraLocalizationServices.Geographic}::StandFusion.StepDescriptionAndCompletion.RequiredFields`,
      '',
      { life: DEFAULT_TOASTER_LIFE },
    );
    return false;
  }

  canLeaveSync(): boolean {
    return true;
  }

  collect(context: IWizardContext): IStepDescriptionAndCompletionData {
    const previousStepData =
      context.getStepState<IStepCreateAreaMaintenanceData>(this.previousStepId)?.data;
    const data: IStepDescriptionAndCompletionData = {
      sourceStep3Snapshot: this.buildStep3Snapshot(previousStepData),
      transformationReason: this.form.get('transformationReason')?.value?.trim() ?? '',
      initialRows: this.initialRows(),
      finalRows: this.finalRows(),
      initialTotalArea: this.initialTotalArea(),
      finalTotalArea: this.finalTotalArea(),
    };

    context.patchStepState(context.activeStepId(), {
      visited: true,
      valid: this.form.valid,
      completed: true,
      data,
    });
    return data;
  }

  fieldInvalid(field: 'transformationReason'): boolean {
    const control = this.form.get(field);
    return !!control && control.invalid && (control.dirty || control.touched);
  }

  formatArea(value: number | null | undefined): string {
    if (value === null || value === undefined) {
      return '-----';
    }

    return value.toFixed(2).replace('.', ',');
  }

  formatCycleLabel(value: string | null | undefined, isLastCycle: boolean): string {
    return this.formatCycleOrRotationLabel(
      value,
      isLastCycle,
      'Geographic::Entity.Cycle.IsLastCycle',
    );
  }

  formatRotationLabel(value: string | null | undefined, isLastRotation: boolean): string {
    return this.formatCycleOrRotationLabel(
      value,
      isLastRotation,
      'Geographic::Entity.Rotation.IsLastRotation',
    );
  }

  private buildStep3Snapshot(value?: IStepCreateAreaMaintenanceData): string {
    return JSON.stringify({
      sourceStep2Snapshot: value?.sourceStep2Snapshot ?? null,
      resultingStand: value?.resultingStand
        ? {
            standBackendId: value.resultingStand.standBackendId ?? null,
            standCycleRotationId: value.resultingStand.standCycleRotationId ?? null,
            currentArea: value.resultingStand.currentArea,
            adjustedArea: this.roundArea(value.resultingStand.adjustedArea),
          }
        : null,
      maintenance: value?.maintenance
        ? {
            period: value.maintenance.period,
            reason: value.maintenance.reason,
            adjustmentType: value.maintenance.adjustmentType,
            details: value.maintenance.details,
            isAreaUpdated: value.maintenance.isAreaUpdated,
          }
        : null,
      areaDelta: value?.areaDelta ?? null,
    });
  }

  private roundArea(value: number): number {
    return Math.round((value + Number.EPSILON) * 100) / 100;
  }

  private formatCycleOrRotationLabel(
    value: string | null | undefined,
    isLastValue: boolean,
    suffixKey: string,
  ): string {
    const formattedValue = value?.trim() ? value : '-----';
    if (!isLastValue) {
      return formattedValue;
    }

    return `${formattedValue} - ${this.localizationService.instant(suffixKey)}`;
  }
}

